/**
 * Maps a taxonomy term to an icon SVG on the WeStay CDN.
 *
 * `amenity_items` and `feature_icons` each have one real file per taxonomy
 * `key` uploaded to `TAXONOMY_ICON_CDN_PATH` — a direct, deterministic lookup,
 * no name matching needed. `staff_roles` has no uploaded set yet, so that one
 * still falls back to keyword matching against the older general CDN library.
 */

import { TAXONOMY_ICON_CDN_PATH } from "@/lib/constants";

const CDN_ICON_BASE = "/cdn-icons";

/** Verified-to-exist filenames on the general CDN library — used by staff roles only. */
const VERIFIED_ICON_FILES = {
  wine: "Wine.svg",
  chef: "Chef.svg",
  wellness: "Wellness.svg",
  hotel: "Hotel.svg",
  villa: "Villa.svg",
  chalet: "Chalet.svg",
  reception: "Reception.svg",
  alarm: "Alarm.svg",
  plant: "Plant.svg",
  taxi: "Taxi.svg",
  ski: "Ski.svg",
  hockey: "Hockey.svg",
  boat: "Boat.svg",
  washingMachine: "Washing Machine.svg",
  user: "User.svg",
  phone: "Phone.svg",
  world: "World.svg",
  picture: "Picture.svg",
} as const;

/** Same idea, but for a person/role rather than a property — used by staff roles. */
const DEFAULT_STAFF_ICON_FILE = VERIFIED_ICON_FILES.user;

/**
 * Ordered keyword → icon rules for staff/service roles — a distinct vocabulary
 * from amenities (people and job functions rather than physical items).
 */
const STAFF_ICON_RULES: Array<{ test: RegExp; file: string }> = [
  { test: /villa ?host|villa ?manager|\bvilla\b/, file: VERIFIED_ICON_FILES.villa },
  { test: /chalet/, file: VERIFIED_ICON_FILES.chalet },
  { test: /concierge|reception|front ?desk/, file: VERIFIED_ICON_FILES.reception },
  { test: /chef|cook/, file: VERIFIED_ICON_FILES.chef },
  { test: /security|guard/, file: VERIFIED_ICON_FILES.alarm },
  { test: /garden|landscap/, file: VERIFIED_ICON_FILES.plant },
  { test: /spa ?therapist|yoga|wellness|massage/, file: VERIFIED_ICON_FILES.wellness },
  { test: /ski/, file: VERIFIED_ICON_FILES.ski },
  { test: /ice ?hockey|hockey/, file: VERIFIED_ICON_FILES.hockey },
  { test: /driver|chauffeur/, file: VERIFIED_ICON_FILES.taxi },
  { test: /sommelier|wine/, file: VERIFIED_ICON_FILES.wine },
  { test: /water ?sports|boat ?captain|\bboat\b|sailing/, file: VERIFIED_ICON_FILES.boat },
  { test: /housekeep|laundry/, file: VERIFIED_ICON_FILES.washingMachine },
  { test: /estate ?manager|residence ?manager|property ?manager/, file: VERIFIED_ICON_FILES.hotel },
];

/** Normalizes taxonomy text for matching: lowercase, underscores/hyphens become spaces. */
function normalize(text: string): string {
  return text.toLowerCase().replace(/[_-]+/g, " ");
}

/** One SVG per taxonomy key, shared by `amenity_items` and `feature_icons`. */
export function getTaxonomyIconUrl(key: string): string {
  return `${TAXONOMY_ICON_CDN_PATH}/${encodeURIComponent(key || "star")}.svg`;
}

/** apple_tv.svg and smart_tv.svg are a UK flag, not a TV — redirect to tv.svg until content-ops fixes the upload. */
const ICON_KEY_OVERRIDES: Record<string, string> = {
  apple_tv: "tv",
  smart_tv: "tv",
};

/** Resolves an `amenity_items` term straight to its matching CDN file. */
export function getAmenityIconUrl(key: string | undefined): string {
  return getTaxonomyIconUrl(ICON_KEY_OVERRIDES[key ?? ""] ?? key ?? "");
}

/** `staff_roles` has no uploaded icon set yet — matched by keyword against the general CDN library instead. */
export function getStaffRoleIconUrl(key: string | undefined, label: string): string {
  const haystack = normalize(`${key ?? ""} ${label}`);
  const rule = STAFF_ICON_RULES.find(({ test }) => test.test(haystack));
  const file = rule?.file ?? DEFAULT_STAFF_ICON_FILE;
  return `${CDN_ICON_BASE}/${encodeURIComponent(file)}`;
}

/**
 * Direct lookup for a specific verified CDN icon — for one-off decorative
 * uses (e.g. a static section icon) rather than matching against a term.
 */
export const CDN_ICONS = {
  phone: `${CDN_ICON_BASE}/${encodeURIComponent(VERIFIED_ICON_FILES.phone)}`,
  world: `${CDN_ICON_BASE}/${encodeURIComponent(VERIFIED_ICON_FILES.world)}`,
  picture: `${CDN_ICON_BASE}/${encodeURIComponent(VERIFIED_ICON_FILES.picture)}`,
} as const;
